import React, { useState } from 'react';
import { Nav, Collapse } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    ShoppingBag,
    ClipboardList,
    Layers,
    Users,
    Truck,
    UserCog,
    BarChart2,
    Settings,
    ChevronRight,
    ChevronDown,
    Store,
    Building2,
    MapPin
} from 'lucide-react';
import { NAV_ITEMS } from '../../utils/constants';
import useAuth from '../../hooks/useAuth';

const Sidebar = ({ isOpen, isMobile, onClose }) => {
    const location = useLocation();
    const { user } = useAuth();
    const [expandedDropdowns, setExpandedDropdowns] = useState({});

    // Ferme le tiroir après un clic sur un lien — uniquement sur mobile
    const handleNavClick = () => {
        if (isMobile && onClose) onClose();
    };

    // Rôles de l'utilisateur (sans préfixe ROLE_)
    const userRoles = user?.roles?.map(role => {
        if (typeof role === 'string') {
            return role.replace('ROLE_', '');
        }
        return role.name.replace('ROLE_', '');
    }) || [];

    // Filtrer les entrées de navigation selon les rôles
    const filteredNavItems = NAV_ITEMS.filter(item => {
        return item.roles.some(role => userRoles.includes(role));
    });

    // Filtrer les enfants selon les rôles
    const filterChildren = (children) => {
        return children.filter(child => {
            return child.roles.some(role => userRoles.includes(role));
        });
    };

    // Ouvrir/fermer un dropdown
    const toggleDropdown = (itemLabel) => {
        setExpandedDropdowns(prev => ({
            ...prev,
            [itemLabel]: !prev[itemLabel]
        }));
    };

    // Le dropdown doit-il être ouvert selon la route courante ?
    const isDropdownActive = (item) => {
        if (!item.children) return false;
        return item.children.some(child => location.pathname.startsWith(child.path));
    };

    // Ouvrir le bon dropdown au montage / changement de route
    React.useEffect(() => {
        const activeDropdowns = {};
        filteredNavItems.forEach(item => {
            if (item.hasDropdown && isDropdownActive(item)) {
                activeDropdowns[item.label] = true;
            }
        });
        setExpandedDropdowns(prev => ({ ...prev, ...activeDropdowns }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Mapper le nom d'icône vers le composant lucide
    const getIcon = (iconName) => {
        switch (iconName) {
            case 'LayoutDashboard':
                return <LayoutDashboard size={20} />;
            case 'ShoppingBag':
                return <ShoppingBag size={20} />;
            case 'ClipboardList':
                return <ClipboardList size={20} />;
            case 'Layers':
                return <Layers size={20} />;
            case 'Users':
                return <Users size={20} />;
            case 'Truck':
                return <Truck size={20} />;
            case 'UserCog':
                return <UserCog size={20} />;
            case 'BarChart2':
                return <BarChart2 size={20} />;
            case 'Settings':
                return <Settings size={20} />;
            case 'Store':
                return <Store size={20} />;
            case 'Building2':
                return <Building2 size={20} />;
            case 'MapPin':
                return <MapPin size={20} />;
            default:
                return <ChevronRight size={20} />;
        }
    };

    const renderNavItem = (item, index) => {
        if (item.hasDropdown && item.children) {
            const filteredChildren = filterChildren(item.children);
            const isExpanded = expandedDropdowns[item.label];
            const isActive = isDropdownActive(item);

            return (
                <div key={index} className="mb-1">
                    <div
                        className={`nav-link py-2 px-3 rounded cursor-pointer d-flex align-items-center justify-content-between ${
                            isActive ? 'active bg-primary text-white' : 'text-white-50'
                        }`}
                        onClick={() => toggleDropdown(item.label)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="d-flex align-items-center">
                            <span className="me-3">{getIcon(item.icon)}</span>
                            <span>{item.label}</span>
                        </div>
                        {isExpanded ? (
                            <ChevronDown size={16} />
                        ) : (
                            <ChevronRight size={16} />
                        )}
                    </div>

                    <Collapse in={isExpanded}>
                        <div className="ms-3">
                            {filteredChildren.map((child, childIndex) => (
                                <NavLink
                                    key={childIndex}
                                    to={child.path}
                                    onClick={handleNavClick}
                                    className={({ isActive }) =>
                                        `nav-link py-2 px-3 mb-1 rounded d-block ${
                                            isActive
                                                ? 'active bg-secondary text-white'
                                                : 'text-white-50 hover-bg-dark'
                                        }`
                                    }
                                    style={{ fontSize: '0.9rem' }}
                                >
                                    <div className="d-flex align-items-center">
                                        <span className="me-3">{getIcon(child.icon)}</span>
                                        <span>{child.label}</span>
                                    </div>
                                </NavLink>
                            ))}
                        </div>
                    </Collapse>
                </div>
            );
        }

        return (
            <NavLink
                key={index}
                to={item.path}
                onClick={handleNavClick}
                className={({ isActive }) =>
                    `nav-link py-2 px-3 mb-1 rounded ${
                        isActive ? 'active bg-primary text-white' : 'text-white-50'
                    }`
                }
            >
                <div className="d-flex align-items-center">
                    <span className="me-3">{getIcon(item.icon)}</span>
                    <span>{item.label}</span>
                </div>
            </NavLink>
        );
    };

    return (
        <div
            className={`sidebar bg-dark text-white ${isOpen ? 'open' : 'closed'}`}
            style={{
                width: isOpen ? '250px' : '0',
                minHeight: '100vh',
                maxHeight: '100vh',
                transition: 'width 0.3s ease',
                overflowX: 'hidden',
                overflowY: 'auto',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: isMobile ? 1045 : 1000   // au-dessus du backdrop (1040) sur mobile
            }}
        >
            <div className="d-flex flex-column p-3" style={{ minHeight: '100vh' }}>
                <div className="p-3 mb-4 border-bottom border-secondary">
                    <h4 className="fw-bold mb-0">Soplast</h4>
                    <div className="text-muted small">Management System</div>
                </div>

                <Nav className="flex-column mb-auto">
                    {filteredNavItems.map((item, index) => renderNavItem(item, index))}
                </Nav>

                <div className="mt-auto p-3 border-top border-secondary">
                    <div className="d-flex align-items-center">
                        <div className="rounded-circle bg-light p-2 me-2">
                            <Users size={18} className="text-dark" />
                        </div>
                        <div>
                            <div className="small text-white-50">Connecté en tant que</div>
                            <div className="fw-semibold">{user?.username || 'User'}</div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .cursor-pointer { cursor: pointer; }
                .hover-bg-dark:hover { background-color: rgba(255, 255, 255, 0.1) !important; }
                .nav-link:hover { background-color: rgba(255, 255, 255, 0.1) !important; }
            `}</style>
        </div>
    );
};

export default Sidebar;